import React, { useContext, useEffect, useState } from "react";
import { ConfigContext } from "./Config";
import { refreshTokenFactory } from "../actions/refreshTokenFactory";
import { UserRole } from "../../common/types";

export interface AuthContextInterface {
  fetchToken: () => Promise<string>;
  authState: AuthState;
  userName: string;
  userId: string;
  userRoles: UserRole[];
}

export enum AuthState {
  Waiting = "waiting",
  Success = "success",
  Failure = "fail",
}

export const AuthContext = React.createContext<AuthContextInterface>({
  fetchToken: () => Promise.resolve(""),
  authState: AuthState.Waiting,
  userName: "",
  userId: "",
  userRoles: [],
});

export const Auth: React.FC = props => {
  const [token, setToken] = useState<string | null>(null);
  const [authState, setAuthState] = useState<AuthState>(AuthState.Waiting);
  const [userName, setUserName] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const { config } = useContext(ConfigContext);
  const { magentoUrl, inventoryUrl } = config;
  const refreshToken = refreshTokenFactory(magentoUrl);

  useEffect(() => {
    fetchToken().catch();
  }, []);

  useEffect(() => {
    if (authState === AuthState.Success) {
      const script = document.createElement("script");

      script.src = `${inventoryUrl}/js/users/embed.js`;
      script.async = true;
      script.addEventListener("load", () => {
        window.addEventListener("message", (event: any) => {
          if (event.data["type"] === "UserSet") {
            // @ts-ignore
            const user = window.app.user.getUser();
            setUserName(user.name);
            setUserId(user.id);
            setUserRoles(user.roles);
          }
        });
      });

      document.body.appendChild(script);
    }
  }, [authState]);

  useEffect(() => {
    if (token) {
      setAuthState(AuthState.Success);
    } else {
      setAuthState(AuthState.Failure);
    }
  }, [token]);

  const fetchToken: () => Promise<string> = async () => {
    const newToken: string = await refreshToken(token);

    if (newToken) {
      setToken(newToken);
    } else {
      setToken(null);
    }

    return newToken;
  };

  const contextValue = {
    fetchToken,
    authState,
    userName,
    userId,
    userRoles,
  };

  switch (authState) {
    case AuthState.Waiting:
      return <h1>Login in...</h1>;

    case AuthState.Failure:
      return <h1>Access Denied. Please login to magento</h1>;

    case AuthState.Success:
      return config && <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>;

    default:
      return <h1>{`Unknown AuthState: ${authState}`}</h1>;
  }
};
